import Vue from 'vue'
import _ from 'lodash'
import {make} from 'vuex-pathify'
import Constants from 'src/utilities/constants'
import {resetResults} from 'src/utilities/vuex2'
import {objectBy, objectById, qbLineItems, formatDateForQuickBooks, searchFieldsHaveValues} from 'src/utilities/utils'
import {dateBetween, esSearch, invoiceClientBuilder} from 'src/utilities/ptracSearch'
import {hydrateTaskSearch} from 'src/utilities/ptracApiHelper'
import {ObjectID} from 'bson'
import {BAD_REQUEST, CREATED, OK, SERVICE_UNAVAILABLE} from 'http-status-codes'
import {ptracGetEntities, ptracPost} from 'src/utilities/ptracApi'
import axios from 'axios'

const initialState = {
  edit: {
    jobId: null,
    refNumber: null,
    customerName: null,
    arAccountRef: null,
    poNumber: null,
    txnDate: null,
    lineItems: [],
    toBePrinted: false,
    toBeEmailed: false
  },
  results: [],
  invoiceDate: formatDateForQuickBooks(new Date()),
  loadingProgress: {
    loaded: 0,
    total: 0,
    percent: 0,
    message: ''
  },
  invoices: {
    clients: []
  },
  isBigData: false,
  uniqueClients: [],
  uniqueItemCodes: [],
  uniqueProjectTypes: [],
}

const state = Vue.util.extend({}, JSON.parse(JSON.stringify(initialState)))

const getters = make.getters(state)
const mutations = make.mutations(state)

getters.searchFieldsHaveValues = (state) => {
  const searchFields = [
    'clientIds',
    'dueBetween'
  ]

  return searchFieldsHaveValues(state.query, searchFields)
}

mutations.resetResults = resetResults
mutations.resetInvoicesClients = (state) => {
  state.invoices.clients = []
}
mutations.updateTask = (state, updatedTask) => {
  const task = objectBy(state.results, 'id', updatedTask.id)

  if (!task) {
    console.error(`Unable to find task by ID ${updatedTask.id}. Task NOT updated. Refresh your screen.`)
    return
  }

  Object.keys(updatedTask).forEach(key => {
    if (task.hasOwnProperty(key)) {
      task[key] = updatedTask[key]
    }
  })
}
mutations.edit = (state, payload) => {
  Object.keys(state.edit).forEach(key => {
    state.edit[key] = payload[key]
  })
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions: {
    async save({state, dispatch}, tasks) {
      const tasksCopy = [...tasks]

      const uniqueClients = _.uniqBy(_.map(tasksCopy, 'job.client'), 'id')

      uniqueClients.forEach((client) => {
        const tasksByClient = _.filter(tasksCopy, (task) => task.job.client.id === client.id)

        delete client.types
        delete client.phone
        delete client.notes
        delete client.addresses
        delete client.contacts
        delete client._links
        delete client.active

        const filteredTasks = []
        tasksByClient.forEach((task) => {
          filteredTasks.push({
            id: task.id,
            jobId: task.jobId,
            projectId: task.projectId,
            name: task.name,
            fieldValues: task.fieldValues
          })
        })

        client.tasks = filteredTasks
        client.invoices = []

        let customerName = _.get(tasksByClient[0], 'job.client.name')
        const jobNumber = _.get(tasksByClient[0], 'job.number')

        if (jobNumber) {
          customerName += ":" + jobNumber
        }

        const toBeEmailed = _.get(tasksByClient[0], 'job.client.quickBooksPreferences.toBeEmailed')
        const toBePrinted = _.get(tasksByClient[0], 'job.client.quickBooksPreferences.toBePrinted')
        const poNumber = _.get(tasksByClient[0], 'fieldValues.poNumber')

        if (client.invoiceByLineItem === true) {
          // generate separate invoices for each client with only 1 line item per invoice
          tasksByClient.forEach(task => {
            const invoice = {
              id: new ObjectID().toString(),
              customerName: customerName,
              arAccountRef: Constants.QUICKBOOKS.AR_ACCOUNT,
              txnDate: state.invoiceDate,
              toBeEmailed: !!toBeEmailed,
              toBePrinted: !!toBePrinted,
              manualInvoice: true,
              lineItems: qbLineItems([task])
            }

            const poNumber = _.get(task, 'fieldValues.poNumber')
            if (poNumber) {
              invoice.poNumber = poNumber
            }
            client.invoices.push(invoice)
          })
        } else {
          // generate one invoice for each client with multiple line items
          client.invoices.push({
            id: new ObjectID().toString(),
            customerName: customerName,
            arAccountRef: Constants.QUICKBOOKS.AR_ACCOUNT,
            txnDate: state.invoiceDate,
            poNumber: poNumber,
            toBeEmailed: !!toBeEmailed,
            toBePrinted: !!toBePrinted,
            manualInvoice: true,
            lineItems: qbLineItems(tasksByClient)
          })
        }

        state.invoices.clients.push(client)
      })

      const url = process.env.VUE_APP_PTRAC_API_URI + '/invoices'

      try {
        const config = Constants.HEADERS.DEFAULT
        config.timeout = Constants.TIME.MILLIS.TEN_MINUTES
        return await axios.post(url, state.invoices, config)
      } catch (err) {
        // horrible hack - axios throwing up with 503 even though the backend is returning a 201.
        // might be because of async initTrackProgress running in a loop
        // haven't been able to find a conclusive answer
        if (SERVICE_UNAVAILABLE === err.response.status) {
          return {
            status: CREATED
          }
        }

        return err.response
      }
    },
    async fetchInvoicesFromPtrac({state}) {
      const clients = state.invoices.clients

      // clients aren't loaded until after the 1st .save(...) call is made
      if (clients.size === 0) {
        return {
          status: OK
        }
      }

      const invoiceIds = []
      clients.forEach((client) => {
        const clientInvoiceIds = _.map(client.invoices, 'id')
        invoiceIds.push(...clientInvoiceIds)
      })

      if (invoiceIds.length === 0) {
        return {
          status: BAD_REQUEST
        }
      }

      return await ptracPost('invoices/findByIdIn', invoiceIds)
    },
    async fetchPtracSearchResults({state, commit, rootGetters, rootState}) {
      if (!rootGetters['security/userId']) {
        console.warn(('userId is not available - not performing search'))
        return
      }

      commit('resetResults')

      const url = `tasks/findPendingInvoiceTasksForAssignee?assigneeId=${rootGetters['security/userId']}`

      const response = await ptracGetEntities(url, 'ptracTasks')

      const allTasks = []

      if (OK === response.status) {
        state.loadingProgress.total = response.data.length

        if (response.data.length > 0) {
          const tasks = await hydrateTaskSearch(response.data, rootState.tasks.templates.results, rootState.users.results, state.loadingProgress)
          const linkedTaskResponse = await ptracPost('tasks/findByIdIn', _.map(tasks, 'linkedTaskId'))
          let linkedTaskIds = _.map(tasks, 'fieldValues.linkedTaskIds')

          state.loadingProgress.percent = 50

          let linkedTasksResponse = {}

          if (linkedTaskIds) {
            linkedTaskIds = linkedTaskIds.filter((linkedTaskId) => linkedTaskId)

            if (linkedTaskIds) {
              linkedTasksResponse = await ptracPost('tasks/findByIdIn', linkedTaskIds.flat())
            }
          }

          state.loadingProgress.percent = 75

          for (const task of tasks) {
            // cast to string to number - so number sorting works
            if (_.get(task, 'fieldValues.price')) {
              task.fieldValues.price = +task.fieldValues.price
            }
            if (_.get(task, 'fieldValues.travelCharge')) {
              task.fieldValues.travelCharge = +task.fieldValues.travelCharge
            }
            if (OK === linkedTaskResponse.status && task.linkedTaskId) {
              const linkedTask = objectById(linkedTaskResponse.data, task.linkedTaskId)
              if (linkedTask) {
                linkedTask.project = task.project
                linkedTask.job = task.job
                task.linkedTask = linkedTask
                task.children = [task.linkedTask]
              }
            }

            if (task.fieldValues && task.fieldValues.linkedTaskIds) {
              task.linkedTasks = []
              for (const linkedTaskId of task.fieldValues.linkedTaskIds) {
                const linkedTask = objectById(linkedTasksResponse.data, linkedTaskId)
                if (!linkedTask) {
                  console.error('Unable to find Linked Task by ID: ' + linkedTaskId)
                } else if (linkedTask.status === Constants.STATUSES.COMPLETED) {
                  linkedTask.job = task.job
                  linkedTask.linkedTask = task
                  task.linkedTasks.push(linkedTask)
                }
              }

              // needed for tree data mode of el-table
              task.children = task.linkedTasks
            }

            allTasks.push(task)

            const uniqueClients = new Map();
            const uniqueItemCodes = new Map();
            const uniqueProjectTypes = new Map();

            for (const task of allTasks) {
              const client = _.get(task, 'job.client')
              if (client) {
                uniqueClients.set(client.id, {
                  value: client.id,
                  label: client.name,
                })
              }

              const itemCode = _.get(task, 'fieldValues.qbItemCode')
              if (itemCode) {
                uniqueItemCodes.set(itemCode, {
                  value: itemCode,
                  label: itemCode
                })
              }

              const project = _.get(task, 'project')

              if (project) {
                uniqueProjectTypes.set(project.projectType, {
                  value: project.projectType,
                  label: project.projectType
                })
              }
            }
            state.uniqueClients = Array.from(uniqueClients.values())
            state.uniqueItemCodes = Array.from(uniqueItemCodes.values())
            state.uniqueProjectTypes = Array.from(uniqueProjectTypes.values())

            state.loadingProgress.message = 'Rendering table, almost there ...'
            state.loadingProgress.loaded = state.loadingProgress.total
            state.loadingProgress.percent = 100

            commit('results', allTasks)
          }
        }
      }

      return response
    }
  }
}
